import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import FooterSection from '../home/components/FooterSection';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const schemaOrg = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/privacy-policy#webpage`,
  url: `${SITE_URL}/privacy-policy`,
  name: 'プライバシーポリシー｜株式会社Y\'s STUDIO',
  description: '株式会社Y\'s STUDIOのプライバシーポリシーです。個人情報の収集・利用・管理方針についてご説明します。',
  inLanguage: 'ja',
  noIndex: true,
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: '株式会社Y\'s STUDIO',
  },
};

export default function PrivacyPolicy() {
  useEffect(() => {
    const id = 'schema-privacy-policy';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schemaOrg);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-24">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">プライバシーポリシー</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-8">
            当社(以下「当社」といいます)は、お客様のプライバシーを尊重し、お客様の個人情報を適切に保護することをお約束いたします。本プライバシーポリシーは、当社のWebサイトおよびAI-OCR自動化サービスをご利用いただく際に収集される個人情報の取り扱いについて説明しています。
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. 収集する情報</h2>
            <p className="text-gray-600 mb-4">当社は、以下の情報を収集することがあります。</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>お客様のお名前、住所、メールアドレス、電話番号などの連絡先情報</li>
              <li>サービスの利用状況、アクセスログ、IPアドレスなどの技術的情報</li>
              <li>お客様の同意を得た上で収集するその他の情報</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. 情報の利用目的</h2>
            <p className="text-gray-600 mb-4">収集した個人情報は、以下の目的で利用いたします。</p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>サービスの提供および改善</li>
              <li>お客様からのお問い合わせへの対応</li>
              <li>法令に基づく義務の履行</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. 情報の第三者提供</h2>
            <p className="text-gray-600">
              当社は、お客様の同意なしに第三者に個人情報を提供することはありません。ただし、法的義務により開示を求められた場合や、業務委託先に提供する場合があります。
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. 情報の管理</h2>
            <p className="text-gray-600">
              当社は、個人情報を適切に管理し、不正アクセス、漏洩、紛失などを防止するために必要な措置を講じます。
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. クッキー(Cookie)の利用</h2>
            <p className="text-gray-600">
              当社のWebサイトでは、クッキーを使用して、サイトの利用状況を把握し、より良いサービス提供を行います。クッキーに関する設定は、ブラウザの設定から変更することができます。
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. 情報の訂正・削除</h2>
            <p className="text-gray-600">
              お客様は、ご自身の個人情報の訂正、削除を要求することができます。これに関するお問い合わせは、下記の連絡先までお願いいたします。
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. お問い合わせ</h2>
            <p className="text-gray-600 mb-4">個人情報の取り扱いに関するご質問やご意見は、以下の連絡先までお問い合わせください。</p>
            <div className="bg-orange-50 p-6 rounded-lg">
              <p className="text-gray-800 mb-2"><strong>会社名：</strong>株式会社Y's STUDIO</p>
              <p className="text-gray-800 mb-2"><strong>住所：</strong>〒657-0041 神戸市灘区琵琶町2-3-6</p>
              <p className="text-gray-800 mb-2">
                <strong>メール：</strong>
                <a href="mailto:ysstudio.kobe@gmail.com" className="text-orange-600 hover:text-orange-700 cursor-pointer ml-2">
                  ysstudio.kobe@gmail.com
                </a>
              </p>
              <p className="text-gray-800">
                <strong>電話：</strong>
                <a href="tel:090-2199-3209" className="text-orange-600 hover:text-orange-700 cursor-pointer ml-2">
                  090-2199-3209
                </a>
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. プライバシーポリシーの変更</h2>
            <p className="text-gray-600">
              当社は、プライバシーポリシーを適宜更新することがあります。更新されたポリシーは、当社のWebサイトに掲載された時点で有効となります。
            </p>
          </section>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-500 text-sm">最終更新日：2025年11月</p>
          </div>
        </div>
      </div>

      <FooterSection />
    </div>
  );
}